import { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { selectClient } from '@client/model/selectors';
import { IntakeType, FundingPolicyType } from '@shared';
import { useIntakeDetection } from './use-intake-detection';
import { useTriggerUnderwriting } from './use-trigger-underwriting';
import { useRequestPolling } from './use-request-polling';
import { underwritingResultsKeys } from '@client/api/waterlily';
import { useToast } from '@client/components/hooks/use-toast';

interface CheckEligibilityOptions {
  fundingPolicyType: FundingPolicyType;
  policyType?: string;
  onShowQuestionnaireDialog?: (variant: 'underwriting' | 'partner-underwriting') => void;
}

export function useCheckEligibility() {
  const queryClient = useQueryClient();
  const client = useSelector(selectClient);
  const clientId = client?.clientId;
  const { data: intakeData, isLoading: isLoadingIntake, refetch } = useIntakeDetection(clientId);

  const {
    mutate: triggerUnderwriting,
    data: triggerData,
    isPending: isTriggeringUnderwriting,
  } = useTriggerUnderwriting();

  const [requestId, setRequestId] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasTriggeredUnderwriting, setHasTriggeredUnderwriting] = useState(false);

  // Store requestId when trigger completes
  useEffect(() => {
    if (triggerData) {
      console.log('useCheckEligibility - setting requestId', { triggerData });
      setRequestId(triggerData);
    }
  }, [triggerData]);

  const { data: pollingData, isPolling } = useRequestPolling(requestId);
  const { toast } = useToast();

  // Watch for polling completion and refetch underwriting results
  useEffect(() => {
    if (pollingData?.status === 'completed' && clientId) {
      queryClient.invalidateQueries({ queryKey: underwritingResultsKeys.detail(clientId) });
      setHasTriggeredUnderwriting(false); // Reset after completion
    }
  }, [pollingData?.status, clientId, queryClient]);

  // Track when underwriting is triggered
  useEffect(() => {
    if (isTriggeringUnderwriting) {
      setHasTriggeredUnderwriting(true);
    }
  }, [isTriggeringUnderwriting]);

  // Reset hasTriggeredUnderwriting when polling starts
  useEffect(() => {
    if (isPolling) {
      setHasTriggeredUnderwriting(false);
    }
  }, [isPolling]);

  const handleCheckEligibility = useCallback(
    async (options: CheckEligibilityOptions) => {
      const { fundingPolicyType, policyType, onShowQuestionnaireDialog } = options;

      setIsProcessing(true);
      try {
        const result = await refetch();

        if (!result.data) {
          toast({
            title: 'Error',
            description: 'Failed to detect intake type. Please try again.',
            variant: 'destructive',
          });
          return;
        }

        const { clientIntakeType, partnerIntakeType } = result.data;
        const isJointPolicy = policyType === 'Joint Life';

        // Check client intake type first
        if (
          clientIntakeType === IntakeType.SHORT_FORM ||
          clientIntakeType === IntakeType.NO_INTAKE
        ) {
          // Show questionnaire dialog for client
          if (onShowQuestionnaireDialog) {
            onShowQuestionnaireDialog('underwriting');
          }
          return;
        }

        // If client has valid intake (FORM_PROVIDER or TYPEFORM), check joint policy
        if (isJointPolicy) {
          // Check if partner exists
          if (!client?.mutableClientPartner) {
            toast({
              title: 'Partner Required',
              description: 'A partner is required for joint policy underwriting analysis.',
              variant: 'destructive',
            });
            return;
          }

          // Check partner intake type
          if (!partnerIntakeType) {
            toast({
              title: 'Partner Intake Required',
              description:
                'Partner intake information is required for joint policy underwriting analysis.',
              variant: 'destructive',
            });
            return;
          }

          if (
            partnerIntakeType === IntakeType.SHORT_FORM ||
            partnerIntakeType === IntakeType.NO_INTAKE
          ) {
            // Show questionnaire dialog for partner
            if (onShowQuestionnaireDialog) {
              onShowQuestionnaireDialog('partner-underwriting');
            }
            return;
          }
        }

        // If we get here, both client (and partner if joint) have valid intake types
        // Trigger underwriting
        triggerUnderwriting({
          fundingPolicyType,
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [refetch, client, triggerUnderwriting, queryClient, clientId],
  );

  // Keep loading state continuous: loading if processing, triggering, or polling, OR in the gap between triggering and polling
  const isLoading =
    isLoadingIntake || isTriggeringUnderwriting || isPolling || isProcessing || hasTriggeredUnderwriting;

  return {
    handleCheckEligibility,
    isLoading,
    clientIntakeType: intakeData?.clientIntakeType,
    partnerIntakeType: intakeData?.partnerIntakeType,
  };
}
